import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';

const VerifyResetCode = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Obtener email del estado de la ubicación o localStorage
  useEffect(() => {
    const userEmail = location.state?.email || localStorage.getItem('resetPasswordEmail');
    if (userEmail) {
      setEmail(userEmail);
    } else {
      // Si no hay email, redirigir a la página de recuperación de contraseña
      navigate('/forgot-password');
    }
  }, [location.state, navigate]);

  // Manejar cuenta regresiva para el botón de reenvío
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Por favor ingrese un código de verificación válido de 6 dígitos');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Verificar el código
      const response = await axios.post('/api/auth/verify-reset-code', {
        email,
        code: verificationCode
      });
      
      setSuccess(true);
      
      // Después de la verificación exitosa, redirigir a la página de restablecimiento de contraseña
      setTimeout(() => {
        navigate('/reset-password', { 
          state: { 
            email,
            verified: true
          } 
        });
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Verificación fallida. Por favor revise su código e intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    
    try {
      await axios.post('/api/auth/forgot-password', { email });
      setCountdown(60); // Deshabilitar botón de reenvío por 60 segundos
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al reenviar el código de verificación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-form-container">
        <div className="auth-header">
          <h2>Verificar Código</h2>
          <p>Hemos enviado un código de verificación a <strong>{email}</strong></p>
        </div>
        
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}
        
        {success && (
          <div className="success-message">
            <i className="fas fa-check-circle"></i> Verificación exitosa! Redirigiendo...
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="verification-code">Código de verificación</label>
            <div className="input-with-icon">
              <i className="fas fa-key input-icon"></i>
              <input
                id="verification-code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').substring(0, 6))}
                disabled={loading || success}
                placeholder="Ingrese código de 6 dígitos"
                maxLength="6"
                required
                autoFocus
                className="verification-code-input"
                onInvalid={(e) => e.target.setCustomValidity('Por favor ingrese el código de verificación')}
                onInput={(e) => e.target.setCustomValidity('')}
              />
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={loading || success || verificationCode.length !== 6}
          >
            {loading ? (
              <>
                <span className="spinner"></span> Verificando...
              </>
            ) : (
              'Verificar Código'
            )}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>
            ¿No recibió el código?{' '}
            {countdown > 0 ? (
              <span>Reenviar en {countdown}s</span>
            ) : (
              <button 
                onClick={handleResendCode} 
                className="btn-link"
                disabled={loading || countdown > 0}
              >
                Reenviar Código
              </button>
            )}
          </p>
          <p className="mt-3">
            <Link to="/forgot-password">Volver atrás</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyResetCode;
